import { Component, Input, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Borrower } from '../../models/borrower-model';
import { DeclarationExplanation, DeclarationExplanationType } from '../../models/declarations-model';
import { EnumsService } from '../../services/enums.service';
import { ConfigurableFieldHostComponent } from '../configurable-field-host.component';

@Component({
    selector: 'borrower-declarations',
    templateUrl: 'borrower-declarations.component.html',
    styleUrls: ['./borrower-declarations.component.scss'],
    standalone: false
})

export class BorrowerDeclarationsComponent extends ConfigurableFieldHostComponent {

  @Input()
  borrower!: Borrower;

  @ViewChild('declarationsForm') declarationsForm: NgForm | undefined;

  // Exposed so the template can reference enum values without string literals.
  public readonly ExplanationType = DeclarationExplanationType;

  constructor(private readonly _enumsService: EnumsService) {
    super();
  }

  validate = () => {
    this.declarationsForm?.form.markAllAsTouched();
    if (this.declarationsForm?.form.valid) {
      return true;
    }
    return false;
  }

  public get propertyTypes() {
    return this._enumsService.propertyTypesForDeclarations;
  }

  public get bankruptcyTypes() {
    return this._enumsService.bankruptyTypes;
  }

  public get heldTitleHows() {
    return this._enumsService.heldTitleHows;
  }

  /**
   * Returns the explanation entry for the given declaration type from
   * `borrower.declarations.explanations`, lazily creating it (and pushing
   * it onto the array) if none exists yet. Called from the template so
   * each Yes-answered declaration has exactly one entry to bind to.
   */
  getExplanation = (type: DeclarationExplanationType): DeclarationExplanation => {
    const declarations = this.borrower.declarations;
    if (!declarations.explanations) {
      declarations.explanations = [];
    }
    let existing = declarations.explanations.find(e => e.explanationType === type);
    if (!existing) {
      existing = { description: '', explanationType: type };
      declarations.explanations.push(existing);
    }
    return existing;
  }

  occupyPropertyChange = (): void => {
    if (!this.borrower.declarations.occupyProperty) {
      this.borrower.declarations.havePropertiesOwnership = null;
      this.borrower.declarations.typeOfProperty = null;
      this.borrower.declarations.heldTitleHow = null;
    }
  }

  havePropertiesOwnershipChange = (): void => {
    if (!this.borrower.declarations.havePropertiesOwnership) {
      this.borrower.declarations.typeOfProperty = null;
      this.borrower.declarations.heldTitleHow = null;
    }
  }

  borrowedDownPaymentChange = (): void => {
    if (!this.borrower.declarations.borrowedDownPayment) {
      this.borrower.declarations.undisclosedMoneyAmount = null;
      this.clearExplanation(DeclarationExplanationType.BorrowedDownPayment);
    }
  }

  declaredBankruptcyChange = (): void => {
    if (!this.borrower.declarations.declaredBankruptcy) {
      this.borrower.declarations.bankruptcyType = null;
      this.clearExplanation(DeclarationExplanationType.DeclaredBankruptcyPastSevenYears);
    }
  }

  /**
   * Generic Yes/No change handler for declarations whose only side effect on
   * Yes→No is clearing the explanation entry. Used by declarations F..L —
   * coSignedLoan, haveJudgement, haveTaxDebt, haveLawsuit,
   * haveConveyedTitleInLieuOfForeclosure, haveCompletedShortSale, beenForclosed.
   * Declarations with extra dependent state (borrowedDownPayment clears
   * undisclosedMoneyAmount, declaredBankruptcy clears bankruptcyType) keep
   * their dedicated handlers above.
   */
  onDeclarationYesNoChange = (isYes: boolean | undefined, type: DeclarationExplanationType): void => {
    if (!isYes) {
      this.clearExplanation(type);
    }
  }

  private clearExplanation(type: DeclarationExplanationType): void {
    const explanations = this.borrower.declarations.explanations;
    if (!explanations) {
      return;
    }
    const index = explanations.findIndex(e => e.explanationType === type);
    if (index >= 0) {
      explanations.splice(index, 1);
    }
  }
}
